/**
 * Fast-Track Application Page - REDESIGNED
 * Focus Mode: Chat is King, Context is Queen
 * Three-Column Layout: Nav | Chat | Context
 */
import { useState, useEffect, useRef } from 'react';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Rocket,
  CheckCircle,
  GraduationCap,
  DollarSign,
  FileText,
  Send,
  Sparkles,
  Building,
  BookOpen,
  Loader2,
  ArrowLeft,
  Save,
  Trash2,
  ChevronDown,
  Upload,
  MapPin,
  Calendar,
  TrendingUp,
  X,
  Menu,
  FileUp,
  Shield,
  ClipboardList,
  BookmarkPlus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Key fields that must be collected BEFORE pushing for documents
const KEY_PROFILE_FIELDS = [
  'preferred_country',
  'preferred_course', 
  'study_level',
  'target_intake',
  'loan_type',
  'english_test_taken',
  'english_score',
  'backlogs'
];

// Check if key profile information is complete (ready to push for docs)
const isKeyInfoComplete = (profile) => {
  if (!profile) return false;
  const requiredFields = KEY_PROFILE_FIELDS;
  let completed = 0;
  requiredFields.forEach(field => {
    const value = profile[field];
    if (value !== null && value !== undefined && value !== '' && value !== 'Not provided' && value !== 'Not specified') {
      completed++;
    }
  });
  // Consider complete if 6 out of 8 key fields are filled (allowing some flexibility)
  return completed >= 6;
};

// Stage configuration
const STAGES = [
  { id: 'initial', name: 'Getting Started', icon: Rocket, requiredFields: ['study_level', 'preferred_course', 'preferred_country'] },
  { id: 'academic_review', name: 'Academics', icon: GraduationCap, requiredFields: ['highest_qualification', 'stream', 'percentage_gpa', 'backlogs'] },
  { id: 'english_review', name: 'English', icon: BookOpen, requiredFields: ['english_test_taken', 'english_score'] },
  { id: 'financial_review', name: 'Financial', icon: DollarSign, requiredFields: ['annual_family_income_lakhs', 'loan_type'] },
  { id: 'university_shortlist', name: 'Universities', icon: Building, requiredFields: ['target_intake'], specialCheck: 'universities' },
  { id: 'application_prep', name: 'Apply', icon: FileText, requiredFields: [], specialCheck: 'application' },
];

// Academic document types that are required for application
const ACADEMIC_DOC_TYPES = ['10th_marksheet', '12th_marksheet', 'degree_certificate', 'transcript', 'academic_certificate'];

// Calculate stage completion with special handling for Universities and Apply stages
const getStageStatus = (stage, profile, shortlistedUniversities = [], applicationSubmitted = false) => {
  // Special handling for Universities stage
  if (stage.specialCheck === 'universities') {
    const hasIntake = profile?.target_intake && profile.target_intake !== '' && profile.target_intake !== 'Not specified';
    const hasUniversities = shortlistedUniversities && shortlistedUniversities.length > 0;
    
    if (hasIntake && hasUniversities) {
      return { completed: 2, total: 2, percent: 100, isComplete: true };
    } else if (hasIntake || hasUniversities) {
      return { completed: 1, total: 2, percent: 50, isComplete: false };
    }
    return { completed: 0, total: 2, percent: 0, isComplete: false };
  }
  
  // Special handling for Apply stage
  if (stage.specialCheck === 'application') {
    if (applicationSubmitted) {
      return { completed: 1, total: 1, percent: 100, isComplete: true };
    }
    return { completed: 0, total: 1, percent: 0, isComplete: false };
  }
  
  // Standard field checking
  if (!profile || !stage.requiredFields?.length) return { completed: 0, total: 0, percent: 0 };
  let completed = 0;
  stage.requiredFields.forEach(field => {
    const value = profile[field];
    if (value !== null && value !== undefined && value !== '' && value !== 'Not provided' && value !== 'Not specified') {
      completed++;
    }
  });
  return {
    completed,
    total: stage.requiredFields.length,
    percent: Math.round((completed / stage.requiredFields.length) * 100),
    isComplete: completed === stage.requiredFields.length
  };
};

// Calculate overall readiness
const calculateReadiness = (profile, documents = []) => {
  if (!profile) return 0;
  const allFields = [
    'study_level', 'preferred_course', 'preferred_country',
    'highest_qualification', 'stream', 'percentage_gpa', 'backlogs',
    'english_test_taken', 'english_score',
    'annual_family_income_lakhs', 'loan_type', 'target_intake'
  ];
  let filled = 0;
  allFields.forEach(field => {
    const value = profile[field];
    if (value !== null && value !== undefined && value !== '' && value !== 'Not provided' && value !== 'Not specified') {
      filled++;
    }
  });
  const basePercent = Math.round((filled / allFields.length) * 100);
  
  // Bonus for verified documents (DigiLocker, IELTS verified)
  const digilockerDocs = documents.filter(doc => doc.verified_via === 'digilocker');
  const digilockerBonus = Math.min(digilockerDocs.length * 3, 10); // +3% per DigiLocker doc, max +10%
  
  // Bonus for verified English score
  const englishVerified = profile?.english_verified ? 5 : 0; // +5% if IELTS/PTE verified via credentials
  
  // Bonus for IT Returns verification (financial proof)
  const itReturnsVerified = documents.some(doc => doc.doc_type === 'it_returns' && doc.verified_via === 'digilocker') ? 5 : 0;
  
  return Math.min(basePercent + digilockerBonus + englishVerified + itReturnsVerified, 100);
};

// Parse AI response to remove markdown formatting
const parseAIResponse = (response) => {
  if (!response) return '';
  // Remove markdown headers like **ACKNOWLEDGE:** **HELPFUL FACT:** **QUESTION:**
  let parsed = response
    .replace(/\*\*ACKNOWLEDGE:\*\*/gi, '')
    .replace(/\*\*HELPFUL FACT:\*\*/gi, '💡 ')
    .replace(/\*\*QUESTION:\*\*/gi, '')
    .replace(/\*\*/g, '')
    .replace(/^\d+\.\s*/gm, '')
    .trim();
  return parsed;
};

const FastTrackApplicationPage = () => {
  const { user, token, loading: authLoading } = useStudentAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const { getLogo } = useAssetContext();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [shortlistedUniversities, setShortlistedUniversities] = useState([]);
  const [selectedUniversities, setSelectedUniversities] = useState(new Set());
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [searchSession, setSearchSession] = useState(null);
  const [searchStatus, setSearchStatus] = useState(null);
  const [savedSearchesList, setSavedSearchesList] = useState([]);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [showDocumentPrompt, setShowDocumentPrompt] = useState(false);
  const [documentPromptShown, setDocumentPromptShown] = useState(false);
  const [universityChangesCount, setUniversityChangesCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [savePromptDismissed, setSavePromptDismissed] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [chatDisabled, setChatDisabled] = useState(false);
  const [showCountryChangeDialog, setShowCountryChangeDialog] = useState(false);
  const [pendingCountryChange, setPendingCountryChange] = useState(null);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  // Constants for anti-misuse
  const MAX_UNIVERSITY_CHANGES = 5;
  const MESSAGES_BEFORE_SAVE_PROMPT = 3;
  const PROFILE_THRESHOLD_FOR_APPLY = 60;
  const MIN_DOCUMENTS_FOR_APPLY = 2;

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/uac-desk/login');
      return;
    }
    loadData();
  }, [user, authLoading]);

  // Auto-resume chat when returning from documents page
  useEffect(() => {
    if (location.state?.autoResume && location.state?.fromDocuments && !loading && profile) {
      // Auto-send a resume message
      const resumeMessage = "I've uploaded my documents. Please continue.";
      setInputMessage('');
      
      // Add welcome back message
      setMessages(prev => [
        ...prev,
        {
          id: `welcome_back_${Date.now()}`,
          role: 'assistant',
          content: `Great! I can see you've uploaded some documents. 📄✅\n\nLet's continue from where we left off. Based on your preferences, shall I proceed with finding universities for you?`
        }
      ]);
      
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loading, profile]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load search status
      const searchStatusRes = await fetch(`${API_URL}/api/search/status/${user.user_id}`);
      let isUserBlocked = false;
      if (searchStatusRes.ok) {
        const statusData = await searchStatusRes.json();
        setSearchStatus(statusData);
        isUserBlocked = statusData.is_blocked;
      }
      
      // Load saved searches
      const savedRes = await fetch(`${API_URL}/api/search/saved/${user.user_id}`);
      if (savedRes.ok) {
        const savedData = await savedRes.json();
        setSavedSearchesList(savedData.saved_searches || []);
      }
      
      // Only start search session if user is not blocked
      if (!isUserBlocked) {
        const sessionRes = await fetch(`${API_URL}/api/search/session/start?user_id=${user.user_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ search_type: 'fast_track', course: null, destination: null })
        });
        if (sessionRes.ok) {
          setSearchSession(await sessionRes.json());
        }
      }
      
      // Load profile
      let profileData = null;
      const profileRes = await fetch(`${API_URL}/api/counselor/profile/${user.user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (profileRes.ok) {
        profileData = await profileRes.json();
        setProfile(profileData);
        
        // Load documents from profile
        if (profileData.documents && typeof profileData.documents === 'object') {
          const docsArray = Object.entries(profileData.documents)
            .filter(([key, value]) => value && value.uploaded)
            .map(([key, value]) => ({
              doc_type: key,
              doc_name: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              filename: value.filename || 'Document',
              uploaded_at: value.uploaded_at || new Date().toISOString()
            }));
          setDocuments(docsArray);
        }
      }

      // Load recommendations - always try, even if blocked
      const recsRes = await fetch(`${API_URL}/api/counselor/recommendations/${user.user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (recsRes.ok) {
        const recsData = await recsRes.json();
        if (recsData.recommendations?.length > 0) {
          setShortlistedUniversities(recsData.recommendations.slice(0, 5).map((u, i) => ({
            id: `rec_${i}`,
            university: u.university,
            program: u.program || 'General Program',
            fee: u.tuition_fee || u.fee || '$25,000/year',
            intake: u.intake || 'Feb/July 2026',
            match_score: u.match_score,
            state: u.state
          })));
        }
      }
      
      // Check if user has any submitted applications
      if (profileData?.applications && profileData.applications.length > 0) {
        setApplicationSubmitted(true);
      }

      // Initial greeting - personalized based on existing profile data
      const hasUniversalData = profileData && (
        profileData.english_test_taken || 
        profileData.highest_qualification || 
        profileData.annual_family_income_lakhs
      );
      
      // If user is blocked, show a special greeting
      let greetingContent;
      if (isUserBlocked) {
        greetingContent = `Welcome back! 🎓\n\nYou've used all your free AI search sessions. However, you can still:\n• View and manage your shortlisted universities\n• Upload documents to complete your profile\n• Apply to universities in your shortlist\n\n📞 **Want more personalized guidance?** Book a free 15-minute call with our expert counselors who can help with your specific questions.\n\n[Schedule Call](https://wa.me/917273928928)`;
      } else if (hasUniversalData) {
        greetingContent = `Hi! Welcome back. I see you already have some profile details saved (academics, English score, financials). These will be used across all your searches.\n\n💡 Did you know? Australian universities offer scholarships up to 30%, and applications get processed in just 2-4 weeks!\n\nWhich country would you like to explore this time - Australia, UK, USA, Canada, or somewhere else?`;
      } else {
        greetingContent = `Hi! I'm here to help you find your perfect university match.\n\n💡 Did you know? Australian universities offer scholarships up to 30%, and applications get processed in just 2-4 weeks!\n\nWhich country interests you most - Australia, UK, USA, Canada, or somewhere else?`;
      }
      
      setMessages([{
        id: 'greeting',
        role: 'assistant',
        content: greetingContent
      }]);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || chatLoading) return;

    // Handle re-engagement after application submission
    if (applicationSubmitted || chatDisabled) {
      const userMessage = { id: `user_${Date.now()}`, role: 'user', content: inputMessage };
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');
      
      // Add polite redirect to counselor
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: `redirect_${Date.now()}`,
          role: 'assistant',
          content: `Thank you for your message! 🙏

Since you've already submitted your application, all further queries will be handled by your assigned counselor.

**Your counselor will:**
• Review your application status
• Answer any questions about the process
• Guide you through next steps

📞 **Contact Options:**
• Wait for your counselor to reach out (within 24 hours)
• Email: support@unixperts.com
• Call: +91-XXXXXXXXXX

We're here to help you every step of the way! 🌟`
        }]);
      }, 500);
      return;
    }

    const userMessage = { id: `user_${Date.now()}`, role: 'user', content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setChatLoading(true);
    
    // Track message count for save prompt
    const newMessageCount = messageCount + 1;
    setMessageCount(newMessageCount);

    try {
      const res = await fetch(`${API_URL}/api/counselor/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: user.user_id,
          message: inputMessage,
          stage: profile?.counseling_stage || 'initial'
        })
      });

      if (res.ok) {
        const data = await res.json();
        
        setMessages(prev => [...prev, {
          id: `ai_${Date.now()}`,
          role: 'assistant',
          content: parseAIResponse(data.response),
          universities: data.universities_matched
        }]);
        
        // Show save prompt after 3 messages (only once, if not dismissed or saved)
        if (newMessageCount >= MESSAGES_BEFORE_SAVE_PROMPT && !showSavePrompt && !savePromptDismissed && searchSession && !searchSession.saved) {
          setShowSavePrompt(true);
        }
        
        // Add matched universities to shortlist
        if (data.universities_matched?.length > 0) {
          setShortlistedUniversities(prev => {
            const existingIds = new Set(prev.map(u => u.university));
            const newUnis = data.universities_matched
              .filter(u => !existingIds.has(u.university))
              .map(u => ({
                id: `uni_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                university: u.university,
                program: u.program || profile?.preferred_course || 'General Program',
                fee: u.fee || u.tuition_fee || '$25,000/year',
                intake: u.intake || profile?.target_intake || 'Feb/July 2026',
                state: u.state,
                match_score: u.match_score
              }));
            return [...prev, ...newUnis].slice(0, 8);
          });
        }
        
        // Refresh profile if fields were extracted (silently, no toast)
        if (data.progress_update?.extracted_fields?.length > 0) {
          const profileRes = await fetch(`${API_URL}/api/counselor/profile/${user.user_id}`);
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            
            // Check if country changed - ask for confirmation before clearing shortlist
            const oldCountry = profile?.preferred_country;
            const newCountry = profileData.preferred_country;
            if (oldCountry && newCountry && oldCountry !== newCountry && shortlistedUniversities.length > 0) {
              // Country changed and we have universities in shortlist - ask user
              setPendingCountryChange({
                oldCountry,
                newCountry,
                newProfile: profileData
              });
              setShowCountryChangeDialog(true);
              // Don't update profile yet - wait for user confirmation
            } else {
              // No country conflict, update normally
              setProfile(profileData);
            }
            
            // Check if key info is complete - push for document collection
            if (!documentPromptShown && isKeyInfoComplete(profileData)) {
              const hasAadhaar = documents.some(d => d.doc_type === 'aadhaar');
              const hasAcademic = documents.some(d => ['10th_marksheet', '12th_marksheet', 'degree_certificate'].includes(d.doc_type));
              
              // Only show document prompt if documents are missing
              if (!hasAadhaar || !hasAcademic) {
                setShowDocumentPrompt(true);
                setDocumentPromptShown(true);
                
                // Add AI message about document collection
                setTimeout(() => {
                  setMessages(prev => [...prev, {
                    id: `doc_prompt_${Date.now()}`,
                    role: 'assistant',
                    content: `Great progress! I now have a clear picture of your profile:\n\n✅ Country: ${profileData.preferred_country || 'Set'}\n✅ Program: ${profileData.preferred_course || 'Set'}\n✅ English: ${profileData.english_test_taken || 'Set'} ${profileData.english_score || ''}\n✅ Financials: ${profileData.loan_type || 'Discussed'}\n\n📋 **Next Step: Document Collection**\n\nTo proceed with your application, I'll need you to upload:\n${!hasAadhaar ? '• Aadhaar Card (mandatory for identity verification)\n' : ''}${!hasAcademic ? '• Academic documents (10th/12th marksheet or degree certificate)\n' : ''}\n\nPlease click the "Upload Documents" button on the right panel to continue.`,
                    isDocumentPrompt: true
                  }]);
                }, 1000);
              }
            }
            
            // Additional prompt when profile hits 60%+ but no documents yet
            const currentReadiness = calculateReadiness(profileData, documents);
            if (currentReadiness >= 60 && documents.length < MIN_DOCUMENTS_FOR_APPLY && !documentPromptShown) {
              setDocumentPromptShown(true);
              setTimeout(() => {
                setMessages(prev => [...prev, {
                  id: `sixty_percent_prompt_${Date.now()}`,
                  role: 'assistant',
                  content: `🎉 **Excellent progress!** Your profile is now at ${currentReadiness}% - you're ready to start applying!\n\n📄 **Just upload ${MIN_DOCUMENTS_FOR_APPLY} documents to unlock applications:**\n• Aadhaar Card (mandatory)\n• Academic document (10th/12th/Degree)\n\nClick "Upload Documents" on the right to continue →`,
                  isDocumentPrompt: true
                }]);
              }, 1500);
            }
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
    }
    setChatLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ========== UNIVERSITY SELECTION FUNCTIONS ==========
  
  const toggleUniversitySelection = (uniId) => {
    setSelectedUniversities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uniId)) {
        newSet.delete(uniId);
      } else {
        newSet.add(uniId);
      }
      return newSet;
    });
  };

  const selectAllUniversities = () => {
    if (selectedUniversities.size === shortlistedUniversities.length) {
      setSelectedUniversities(new Set());
    } else {
      setSelectedUniversities(new Set(shortlistedUniversities.map(u => u.id)));
    }
  };

  const removeUniversity = (uniId) => {
    if (universityChangesCount >= MAX_UNIVERSITY_CHANGES) {
      toast.error(`Maximum ${MAX_UNIVERSITY_CHANGES} university changes allowed per session. Start a new search for more changes.`);
      return;
    }
    setShortlistedUniversities(prev => prev.filter(u => u.id !== uniId));
    setSelectedUniversities(prev => {
      const newSet = new Set(prev);
      newSet.delete(uniId);
      return newSet;
    });
    setUniversityChangesCount(prev => prev + 1);
  };

  const applyToSelected = async () => {
    const selectedUnis = shortlistedUniversities.filter(u => selectedUniversities.has(u.id));
    if (selectedUnis.length === 0) {
      toast.error('Please select at least one university to apply');
      return;
    }
    
    // Check eligibility
    const readiness = calculateReadiness(profile, documents);
    if (readiness < PROFILE_THRESHOLD_FOR_APPLY) {
      toast.error(`Profile must be at least ${PROFILE_THRESHOLD_FOR_APPLY}% complete to apply. Current: ${readiness}%`);
      return;
    }
    
    if (documents.length < MIN_DOCUMENTS_FOR_APPLY) {
      toast.error(`Please upload at least ${MIN_DOCUMENTS_FOR_APPLY} documents before applying`);
      return;
    }
    
    // Check for academic documents specifically
    const hasAcademicDoc = documents.some(doc => ACADEMIC_DOC_TYPES.includes(doc.doc_type));
    if (!hasAcademicDoc) {
      toast.error('Please upload at least one academic document (10th/12th marksheet, degree certificate, or transcript) before applying');
      return;
    }
    
    // Check for identity document (Aadhaar)
    const hasIdentityDoc = documents.some(doc => doc.doc_type === 'aadhaar' || doc.doc_type === 'passport');
    if (!hasIdentityDoc) {
      toast.error('Please upload an identity document (Aadhaar or Passport) before applying');
      return;
    }
    
    // Create application records in backend using new applications API
    const createdApplications = [];
    try {
      for (const uni of selectedUnis) {
        const res = await fetch(`${API_URL}/api/applications/create/${user.user_id}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            university_name: uni.university,
            program: uni.program || 'General Program',
            intake: uni.intake || 'Feb/July 2026',
            country: profile?.preferred_country || 'Australia',
            notes: `Applied via Fast-Track Application. Match score: ${uni.match_score || 'N/A'}%`
          })
        });
        
        if (res.ok) {
          const data = await res.json();
          createdApplications.push(data);
        }
      }
      
      // Also call the legacy endpoint for backwards compatibility
      await fetch(`${API_URL}/api/counselor/applications/submit`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: user.user_id,
          universities: selectedUnis.map(u => ({
            university: u.university,
            program: u.program,
            intake: u.intake
          })),
          profile_readiness: readiness
        })
      });
      
    } catch (error) {
      console.error('Application submission error:', error);
      // Continue anyway - show the completion message
    }
    
    // Mark chat as completed and show next steps
    setApplicationSubmitted(true);
    setChatDisabled(true);
    
    const uniNames = selectedUnis.map(u => u.university).join(', ');
    
    // Add final AI message with next steps
    setMessages(prev => [...prev, {
      id: `application_complete_${Date.now()}`,
      role: 'assistant',
      content: `🎉 **Congratulations! Your application has been submitted!**

You've applied to: **${uniNames}**

---

**📋 Next Steps:**

1. **📞 Counselor Meeting** - A dedicated counselor will be assigned to you within 24 hours. They will contact you to schedule an introductory call.

2. **✅ Document Validation** - Your uploaded documents will be verified by our team. You may be asked for additional documents if needed.

3. **📄 Pending Documents** - Please prepare the following if not already uploaded:
   • Passport (front & back)
   • Academic transcripts (official)
   • English proficiency scorecard (IELTS/TOEFL/PTE)
   • Statement of Purpose (SOP)
   • Letters of Recommendation (LOR)
   • Financial documents (bank statements, loan approval if applicable)

4. **📝 Universal Application Form** - Your counselor will help you complete the detailed application form for each university.

5. **💰 Education Loan Process** - If you've opted for an education loan, our financial counselor will guide you through the loan application process with our partner banks.

---

**🎯 Track Your Applications**
Visit your [Application Tracker](/uac-desk/applications) to monitor the progress of each application in real-time!

**💬 Need Help?**
For any queries, please reach out to your assigned counselor directly. This AI chat session is now complete.

We're excited to help you achieve your study abroad dreams! 🌟`,
      isApplicationComplete: true
    }]);
    
    toast.success(`Application submitted for ${selectedUnis.length} universities! Track progress in your Applications dashboard.`);
  };

  // ========== SAVE TO DRAFTS/BOOKMARKS FUNCTION ==========
  
  const saveToDrafts = async (status) => {
    const selectedUnis = shortlistedUniversities.filter(u => selectedUniversities.has(u.id));
    if (selectedUnis.length === 0) {
      toast.error('Please select at least one university');
      return;
    }
    
    const savedCount = { success: 0, failed: 0 };
    
    for (const uni of selectedUnis) {
      try {
        const res = await fetch(`${API_URL}/api/applications/draft/${user.user_id}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            university_name: uni.university,
            university_slug: uni.id,
            program: uni.program || 'General Program',
            country: profile?.preferred_country || 'Australia',
            tuition_fee: uni.fee,
            match_score: uni.match_score,
            status: status, // 'draft' or 'bookmarked'
            notes: `Saved from AI Finder. Match score: ${uni.match_score || 'N/A'}%`
          })
        });
        
        if (res.ok) {
          savedCount.success++;
        } else {
          savedCount.failed++;
        }
      } catch (error) {
        console.error('Error saving draft:', error);
        savedCount.failed++;
      }
    }
    
    if (savedCount.success > 0) {
      const action = status === 'draft' ? 'saved as drafts' : 'bookmarked';
      toast.success(`${savedCount.success} course${savedCount.success > 1 ? 's' : ''} ${action}!`);
      
      // Add AI message
      setMessages(prev => [...prev, {
        id: `saved_${Date.now()}`,
        role: 'assistant',
        content: status === 'draft' 
          ? `✅ **${savedCount.success} course${savedCount.success > 1 ? 's' : ''} saved to your Drafts!**\n\nYou can now:\n1. Go to [My Applications](/uac-desk/applications) to complete them\n2. Select your intake term & year\n3. Upload required documents\n4. Submit to your counselor when ready\n\nWould you like to explore more universities?`
          : `📑 **${savedCount.success} course${savedCount.success > 1 ? 's' : ''} saved for later!**\n\nThese are bookmarked in your [My Applications](/uac-desk/applications) page. When you're ready, move them to Drafts to start the application process.\n\nShall I help you find more options?`
      }]);
      
      // Clear selections
      setSelectedUniversities(new Set());
    }
    
    if (savedCount.failed > 0) {
      toast.error(`Failed to save ${savedCount.failed} course${savedCount.failed > 1 ? 's' : ''}`);
    }
  };

  // ========== SAVE SEARCH FUNCTIONS ==========
  
  const saveSearch = async (searchName) => {
    if (!searchSession?.session_id) {
      toast.error('No active search session');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/search/session/${searchSession.session_id}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          session_id: searchSession.session_id,
          session_name: searchName 
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Search saved!');
        setShowSavePrompt(false);
        setSavePromptDismissed(true); // Prevent prompt from appearing again
        
        // Update search session to mark as saved
        setSearchSession(prev => prev ? { ...prev, saved: true, session_name: searchName } : prev);
        
        // Refresh saved searches list
        const savedRes = await fetch(`${API_URL}/api/search/saved/${user.user_id}`);
        if (savedRes.ok) {
          const savedData = await savedRes.json();
          setSavedSearchesList(savedData.saved_searches || []);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save search');
      }
    } catch (error) {
      toast.error('Failed to save search');
    }
  };

  const clearSearch = async () => {
    // Clear search-specific data (country, course, intake, universities)
    // Keep universal data (academics, English, financials)
    try {
      // Reset search-specific fields in backend
      await fetch(`${API_URL}/api/counselor/profile/${user.user_id}/reset-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Refresh profile to get updated data
      const profileRes = await fetch(`${API_URL}/api/counselor/profile/${user.user_id}`);
      if (profileRes.ok) {
        setProfile(await profileRes.json());
      }
    } catch (error) {
      console.error('Error resetting search fields:', error);
    }
    
    // Reset chat and university shortlist
    setMessages([{
      id: 'greeting',
      role: 'assistant',
      content: "Hi! Starting a fresh search. Your academic, English, and financial details are preserved.\n\n💡 Did you know? Australian universities offer scholarships up to 30%, and applications get processed in just 2-4 weeks!\n\nWhich country interests you most - Australia, UK, USA, Canada, or somewhere else?"
    }]);
    setShortlistedUniversities([]);
    setSelectedUniversities(new Set());
    setUniversityChangesCount(0);
    setMessageCount(0);
    setShowSavePrompt(false);
    setDocumentPromptShown(false);
    toast.success('New search started! Your profile details are preserved.');
  };

  // Handle country change confirmation
  const handleCountryChangeConfirm = () => {
    if (pendingCountryChange) {
      // User confirmed - switch to new country and clear university shortlist
      setProfile(pendingCountryChange.newProfile);
      setShortlistedUniversities([]);
      setSelectedUniversities(new Set());
      setUniversityChangesCount(0);
      
      // Add a message acknowledging the switch
      setMessages(prev => [...prev, {
        id: `country_switch_${Date.now()}`,
        role: 'assistant',
        content: `Got it! I've switched your search to **${pendingCountryChange.newCountry}**. 🌍\n\nYour academic details, English score, and financial information are preserved. I've cleared your previous university shortlist so we can find the best ${pendingCountryChange.newCountry} universities for you!\n\nWhat field or program are you interested in studying?`
      }]);
      
      toast.success(`Switched to ${pendingCountryChange.newCountry}`);
    }
    setPendingCountryChange(null);
    setShowCountryChangeDialog(false);
  };

  const handleCountryChangeCancel = () => {
    // User wants to keep original country - don't update profile
    setPendingCountryChange(null);
    setShowCountryChangeDialog(false);
    
    // Add message that we're staying with original country
    const oldCountry = pendingCountryChange?.oldCountry;
    if (oldCountry) {
      setMessages(prev => [...prev, {
        id: `country_keep_${Date.now()}`,
        role: 'assistant',
        content: `No problem! We'll continue with your ${oldCountry} search and keep your current university shortlist. How can I help you further?`
      }]);
    }
  };

  const resumeSavedSearch = async (search) => {
    try {
      const sessionRes = await fetch(`${API_URL}/api/search/session/${search.session_id}`);
      if (sessionRes.ok) {
        const sessionData = await sessionRes.json();
        setSearchSession(sessionData);
        setMessages([{
          id: 'resume_greeting',
          role: 'assistant',
          content: `Welcome back! Resuming "${search.session_name}" - your profile is at ${search.completion_percentage || 0}% completion. What would you like to explore?`
        }]);
        setShowSavedSearches(false);
        toast.success(`Resumed: "${search.session_name}"`);
      }
    } catch (error) {
      toast.error('Failed to resume search');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-ux-blue to-ux-cyan flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <p className="text-slate-500 font-medium">Loading your journey...</p>
        </div>
      </div>
    );
  }

  const readinessPercent = calculateReadiness(profile, documents);
  const hasAadhaar = documents.some(d => d.doc_type === 'aadhaar');
  const hasIdentity = documents.some(d => d.doc_type === 'aadhaar' || d.doc_type === 'passport');
  const hasAcademic = documents.some(d => ACADEMIC_DOC_TYPES.includes(d.doc_type));
  
  // Apply eligibility: Profile 60%+ AND at least 2 documents AND identity doc AND academic doc
  const canApply = readinessPercent >= PROFILE_THRESHOLD_FOR_APPLY && 
                   documents.length >= MIN_DOCUMENTS_FOR_APPLY && 
                   hasIdentity && 
                   hasAcademic;

  return (
    <TooltipProvider>
      <div className="h-screen flex flex-col lg:flex-row bg-slate-50 overflow-hidden" data-testid="fast-track-page">
        
        {/* ===== MAIN CHAT AREA (60% on desktop) ===== */}
        <div className="flex-1 flex flex-col min-w-0 lg:border-r border-slate-200">
          
          {/* Header */}
          <header className="flex-shrink-0 bg-white border-b border-slate-200 px-4 lg:px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              {/* Left: Back Button + Logo + Title */}
              <div className="flex items-center gap-3 min-w-0">
                <Link
                  to="/uac-desk/dashboard"
                  className="flex items-center gap-2 text-slate-600 hover:text-ux-blue transition-colors"
                  data-testid="back-to-dashboard"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Dashboard</span>
                </Link>
                <div className="h-6 w-px bg-slate-200" />
                <img 
                  src={getLogo()} 
                  alt="UniXperts" 
                  className={`h-7 w-auto object-contain cursor-pointer hidden sm:block ${isDark ? 'brightness-0 invert' : ''}`}
                  onClick={() => navigate('/uac-desk/dashboard')}
                />
                <div className="min-w-0">
                  <h1 className="text-lg lg:text-xl font-bold text-slate-900 font-display truncate">
                    AI Career Counselor
                  </h1>
                  <p className="text-xs text-slate-500 hidden sm:block">Personalized guidance</p>
                </div>
              </div>
              
              {/* Right: Actions */}
              <div className="flex items-center gap-2">
                {searchStatus && (
                  <span className="text-xs text-slate-500 hidden lg:inline">
                    {searchStatus.searches_remaining} searches left
                  </span>
                )}
                
                {/* Saved Searches Dropdown */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-slate-600 border-slate-200"
                    onClick={() => setShowSavedSearches(!showSavedSearches)}
                    data-testid="saved-searches-btn"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Saved</span>
                    {savedSearchesList.length > 0 && (
                      <Badge className="bg-ux-blue text-white text-[10px] h-4 px-1 ml-1">
                        {savedSearchesList.length}
                      </Badge>
                    )}
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                  
                  {showSavedSearches && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
                      <div className="p-3 border-b border-slate-100 bg-slate-50">
                        <h4 className="font-semibold text-slate-800 text-sm">Saved Searches</h4>
                      </div>
                      {savedSearchesList.length === 0 ? (
                        <div className="p-4 text-center text-sm text-slate-500">
                          No saved searches yet
                        </div>
                      ) : (
                        <div className="max-h-48 overflow-y-auto">
                          {savedSearchesList.map((search, idx) => (
                            <button
                              key={search.session_id || idx}
                              onClick={() => resumeSavedSearch(search)}
                              className="w-full p-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                            >
                              <p className="font-medium text-slate-800 text-sm truncate">
                                {search.session_name || `Search ${idx + 1}`}
                              </p>
                              <p className="text-xs text-slate-500">{search.completion_percentage || 0}% complete</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                  onClick={clearSearch}
                  data-testid="clear-search-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                {/* Mobile: Context Panel Toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setMobileDrawerOpen(true)}
                >
                  <Menu className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Stage Tracker - Horizontal Pills */}
            <div className="mt-4 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
              {STAGES.map((stage) => {
                const status = getStageStatus(stage, profile, shortlistedUniversities, applicationSubmitted);
                const Icon = stage.icon;
                return (
                  <Tooltip key={stage.id}>
                    <TooltipTrigger asChild>
                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all cursor-default min-w-fit
                          ${status.isComplete 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : status.percent > 0
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'bg-slate-100 text-slate-500 border border-slate-200'
                          }`}
                      >
                        {status.isComplete ? (
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Icon className="w-3.5 h-3.5" />
                        )}
                        <span>{stage.name}</span>
                        {!status.isComplete && status.total > 0 && (
                          <span className="text-[10px] opacity-70">{status.percent}%</span>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{status.isComplete ? 'Complete!' : `${status.completed}/${status.total} ${stage.specialCheck ? 'items' : 'fields'} done`}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </header>

          {/* Chat Messages */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            data-testid="chat-panel"
          >
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] lg:max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-ux-blue text-white rounded-tr-sm shadow-lg shadow-blue-100'
                      : 'bg-white text-slate-800 rounded-tl-sm shadow-sm border border-slate-100'
                  }`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                        <Sparkles className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-xs font-medium text-slate-500">AI Counselor</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  
                  {/* Inline University Suggestions */}
                  {msg.universities?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs font-medium text-slate-500 mb-2">Recommended for you:</p>
                      <div className="flex flex-wrap gap-2">
                        {msg.universities.slice(0, 3).map((uni, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-lg"
                          >
                            <Building className="w-3 h-3" />
                            {uni.university}
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1">{uni.match_score}%</Badge>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                    <span className="text-sm text-slate-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Chat Input - Sticky Bottom */}
          <div className="flex-shrink-0 p-4 lg:p-6 bg-gradient-to-t from-slate-50 via-slate-50 to-transparent">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-lg shadow-slate-100 focus-within:ring-2 focus-within:ring-ux-blue/20 focus-within:border-ux-blue transition-all">
              <Input
                placeholder="Ask about universities, requirements, intakes..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 border-0 bg-transparent focus-visible:ring-0 px-0 text-sm text-slate-900 placeholder:text-slate-400"
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || chatLoading}
                size="sm"
                className="rounded-full w-9 h-9 p-0 bg-ux-blue hover:bg-ux-blue/90 disabled:opacity-50"
                data-testid="chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* ===== CONTEXT PANEL (Right Sidebar - Desktop) ===== */}
        <aside className={`
          w-full lg:w-80 xl:w-96 bg-white flex-shrink-0 flex flex-col
          fixed inset-0 lg:relative z-50 lg:z-auto
          transform transition-transform duration-300 ease-out
          ${mobileDrawerOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}>
          {/* Mobile Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 lg:hidden">
            <h3 className="font-semibold text-slate-900">Your Progress</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileDrawerOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4 lg:p-5 space-y-5" style={{ scrollbarWidth: 'none' }}>
            
            {/* Profile Readiness Ring */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-5 border border-slate-100">
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20 flex-shrink-0">
                  <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50" cy="50" r="42"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="8"
                    />
                    <circle
                      cx="50" cy="50" r="42"
                      fill="none"
                      stroke={readinessPercent >= 80 ? '#10b981' : readinessPercent >= 50 ? '#0854e2' : '#f59e0b'}
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${readinessPercent * 2.64} 264`}
                      className="transition-all duration-700"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold font-display text-slate-800">{readinessPercent}%</span>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Profile Readiness</h4>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {readinessPercent >= 80 ? 'Ready to apply!' : readinessPercent >= 50 ? 'Good progress' : 'Keep going'}
                  </p>
                </div>
              </div>
            </div>

            {/* Required Documents */}
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                  <FileUp className="w-4 h-4 text-ux-blue" />
                  Required to Apply
                </h4>
              </div>
              <div className="p-4 space-y-2">
                {/* Minimum Required: Aadhaar + Academic */}
                <p className="text-xs text-slate-500 mb-3">Minimum {MIN_DOCUMENTS_FOR_APPLY} documents required</p>
                
                {/* Aadhaar - Mandatory */}
                <div className={`flex items-center gap-3 p-3 rounded-xl border ${hasAadhaar ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  {hasAadhaar ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-amber-400 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <span className={`text-sm font-medium ${hasAadhaar ? 'text-emerald-700' : 'text-amber-700'}`}>
                      Aadhaar Card
                    </span>
                    <p className="text-xs text-slate-500">Upload manually</p>
                  </div>
                </div>
                
                {/* Academic Document */}
                <div className={`flex items-center gap-3 p-3 rounded-xl border ${hasAcademic ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  {hasAcademic ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-amber-400 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <span className={`text-sm font-medium ${hasAcademic ? 'text-emerald-700' : 'text-amber-700'}`}>
                      Academic Document
                    </span>
                    <p className="text-xs text-slate-500">10th/12th/Degree certificate</p>
                  </div>
                </div>
                
                {/* Optional: IT Returns for financial verification */}
                {readinessPercent >= 50 && (
                  <div className={`flex items-center gap-3 p-3 rounded-xl border bg-slate-50 border-slate-200`}>
                    <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-slate-600">
                        IT Returns (Optional)
                      </span>
                      <p className="text-xs text-slate-400">For financial verification</p>
                    </div>
                  </div>
                )}
                
                {/* IELTS Verification hint */}
                {profile?.english_test_taken && !profile?.english_verified && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-2">
                    <p className="text-xs text-blue-700">
                      <span className="font-medium">Tip:</span> Verify your {profile.english_test_taken} score for +5% profile boost!
                    </p>
                  </div>
                )}
                
                {/* 60% Prompt Banner */}
                {readinessPercent >= 60 && documents.length < MIN_DOCUMENTS_FOR_APPLY && (
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-3 mt-3">
                    <p className="text-sm font-medium text-emerald-800">
                      🎉 Profile at {readinessPercent}%! 
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      Upload {MIN_DOCUMENTS_FOR_APPLY - documents.length} more document{MIN_DOCUMENTS_FOR_APPLY - documents.length > 1 ? 's' : ''} to start applying!
                    </p>
                  </div>
                )}
                
                <Button 
                  className="w-full mt-3 bg-ux-blue hover:bg-ux-blue/90"
                  onClick={() => navigate('/uac-desk/documents', { state: { returnTo: location.pathname } })}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Documents
                </Button>
              </div>
            </div>

            {/* Verification Section - Make Your Application Stronger */}
            <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl border border-indigo-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-indigo-100 bg-indigo-50/50">
                <h4 className="font-semibold text-indigo-800 text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4 text-indigo-600" />
                  Make Your Application Stronger
                </h4>
                <p className="text-xs text-indigo-600 mt-0.5">Verified credentials boost your profile</p>
              </div>
              <div className="p-4 space-y-3">
                {/* English Score Verification */}
                {profile?.english_test_taken && (
                  <div className={`flex items-center justify-between p-3 rounded-xl border ${
                    profile?.english_verified 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-white border-slate-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {profile?.english_verified ? (
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <BookOpen className="w-5 h-5 text-slate-400" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {profile.english_test_taken} Score
                        </p>
                        <p className="text-xs text-slate-500">
                          {profile?.english_verified ? 'Verified ✓' : '+5% profile boost'}
                        </p>
                      </div>
                    </div>
                    {!profile?.english_verified && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        onClick={() => {
                          toast.info('IELTS/PTE verification coming soon! This feature requires official testing portal integration.');
                        }}
                      >
                        Verify
                      </Button>
                    )}
                  </div>
                )}
                
                {/* DigiLocker Academic Verification */}
                <div className={`flex items-center justify-between p-3 rounded-xl border ${
                  documents.some(d => d.verified_via === 'digilocker')
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-white border-slate-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {documents.some(d => d.verified_via === 'digilocker') ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <GraduationCap className="w-5 h-5 text-slate-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        Academic Documents
                      </p>
                      <p className="text-xs text-slate-500">
                        {documents.some(d => d.verified_via === 'digilocker') 
                          ? 'DigiLocker Verified ✓' 
                          : '+3% per doc via DigiLocker'}
                      </p>
                    </div>
                  </div>
                  {!documents.some(d => d.verified_via === 'digilocker') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      onClick={() => {
                        toast.info('DigiLocker integration coming soon! This will auto-fetch your verified documents.');
                      }}
                    >
                      Connect
                    </Button>
                  )}
                </div>
                
                {/* IT Returns Verification */}
                <div className={`flex items-center justify-between p-3 rounded-xl border ${
                  documents.some(d => d.doc_type === 'it_returns' && d.verified_via === 'digilocker')
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-white border-slate-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {documents.some(d => d.doc_type === 'it_returns' && d.verified_via === 'digilocker') ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <DollarSign className="w-5 h-5 text-slate-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        IT Returns
                      </p>
                      <p className="text-xs text-slate-500">
                        {documents.some(d => d.doc_type === 'it_returns' && d.verified_via === 'digilocker')
                          ? 'DigiLocker Verified ✓' 
                          : '+5% via DigiLocker'}
                      </p>
                    </div>
                  </div>
                  {!documents.some(d => d.doc_type === 'it_returns' && d.verified_via === 'digilocker') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      onClick={() => {
                        toast.info('DigiLocker integration coming soon! This will auto-fetch your IT Returns.');
                      }}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* University Shortlist */}
            {shortlistedUniversities.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                  <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                    <Building className="w-4 h-4 text-violet-600" />
                    Your Shortlist ({shortlistedUniversities.length})
                  </h4>
                  <button
                    onClick={selectAllUniversities}
                    className="text-xs text-ux-blue hover:underline"
                  >
                    {selectedUniversities.size === shortlistedUniversities.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
                  {shortlistedUniversities.map((uni) => {
                    const isSelected = selectedUniversities.has(uni.id);
                    return (
                      <div key={uni.id} className={`p-3 transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleUniversitySelection(uni.id)}
                            className="mt-1 w-4 h-4 rounded border-slate-300 text-ux-blue focus:ring-ux-blue"
                          />
                          
                          {/* University Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h5 className="font-medium text-slate-900 text-sm truncate">{uni.university}</h5>
                              <button
                                onClick={() => removeUniversity(uni.id)}
                                className="text-slate-400 hover:text-rose-500 p-1 -mr-1"
                                title="Remove from shortlist"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <p className="text-xs text-ux-blue mt-0.5 truncate">{uni.program}</p>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                {uni.fee}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {uni.intake}
                              </span>
                            </div>
                          </div>
                          
                          {/* Match Score */}
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0
                            ${uni.match_score >= 80 ? 'bg-emerald-100 text-emerald-700' : 
                              uni.match_score >= 60 ? 'bg-blue-100 text-blue-700' : 
                              'bg-amber-100 text-amber-700'}`}>
                            {uni.match_score}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Changes Remaining Notice */}
                {universityChangesCount > 0 && (
                  <div className="px-4 py-2 bg-amber-50 border-t border-amber-100">
                    <p className="text-xs text-amber-700">
                      {MAX_UNIVERSITY_CHANGES - universityChangesCount} changes remaining
                    </p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="p-4 border-t border-slate-100 bg-slate-50 space-y-2">
                  {selectedUniversities.size > 0 && (
                    <>
                      {/* Draft to Apply Button */}
                      <Button 
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                        onClick={() => saveToDrafts('draft')}
                      >
                        <ClipboardList className="w-4 h-4 mr-2" />
                        Draft to Apply ({selectedUniversities.size})
                      </Button>
                      
                      {/* Bookmark Button */}
                      <Button 
                        variant="outline"
                        className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
                        onClick={() => saveToDrafts('bookmarked')}
                      >
                        <BookmarkPlus className="w-4 h-4 mr-2" />
                        Save for Later ({selectedUniversities.size})
                      </Button>
                      
                      <p className="text-xs text-slate-500 text-center pt-1">
                        <span className="font-medium">Draft to Apply:</span> Add to your application list
                        <br />
                        <span className="font-medium">Save for Later:</span> Bookmark for future consideration
                      </p>
                    </>
                  )}
                  
                  {selectedUniversities.size === 0 && (
                    <p className="text-xs text-slate-500 text-center py-2">
                      Select universities above to save them
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Empty State for Universities */}
            {shortlistedUniversities.length === 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Building className="w-6 h-6 text-slate-400" />
                </div>
                <h4 className="font-medium text-slate-700 text-sm">Your Shortlist</h4>
                <p className="text-xs text-slate-500 mt-1">
                  Universities will appear here as you chat with the AI counselor
                </p>
              </div>
            )}
            
            {/* Urgency Banner */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
              <p className="text-sm font-medium text-amber-800">
                🎯 Feb 2026 intake closing soon!
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Most universities process applications in 2-4 weeks.
              </p>
            </div>
          </div>
        </aside>

        {/* Mobile Overlay */}
        {mobileDrawerOpen && (
          <div 
            className="fixed inset-0 bg-black/30 z-40 lg:hidden"
            onClick={() => setMobileDrawerOpen(false)}
          />
        )}
        
        {/* Save Search Prompt Modal */}
        {showSavePrompt && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={() => { setShowSavePrompt(false); setSavePromptDismissed(true); }} />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm">
              <div className="bg-white rounded-2xl shadow-xl p-6 mx-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-ux-blue/10 flex items-center justify-center">
                    <Save className="w-5 h-5 text-ux-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Save Your Search?</h3>
                    <p className="text-xs text-slate-500">You can resume it later</p>
                  </div>
                </div>
                
                <input
                  type="text"
                  id="search-name-input"
                  placeholder="Enter a name for this search..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 bg-white focus:ring-2 focus:ring-ux-blue/20 focus:border-ux-blue mb-4 placeholder:text-slate-400"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      saveSearch(e.target.value.trim());
                    }
                  }}
                />
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-100"
                    onClick={() => { setShowSavePrompt(false); setSavePromptDismissed(true); }}
                  >
                    Later
                  </Button>
                  <Button
                    className="flex-1 bg-ux-blue hover:bg-ux-blue/90 text-white"
                    onClick={() => {
                      const input = document.getElementById('search-name-input');
                      if (input?.value?.trim()) {
                        saveSearch(input.value.trim());
                      } else {
                        toast.error('Please enter a name for your search');
                      }
                    }}
                  >
                    Save
                  </Button>
                </div>
                
                {searchStatus && (
                  <p className="text-xs text-slate-500 text-center mt-3">
                    {searchStatus.searches_remaining} saved searches remaining
                  </p>
                )}
              </div>
            </div>
          </>
        )}
        
        {/* Country Change Confirmation Dialog */}
        {showCountryChangeDialog && pendingCountryChange && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={handleCountryChangeCancel} />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm">
              <div className="bg-white rounded-2xl shadow-xl p-6 mx-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Change Destination?</h3>
                    <p className="text-xs text-slate-500">This will affect your shortlist</p>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 mb-4">
                  You mentioned <strong>{pendingCountryChange.newCountry}</strong>, but you're currently exploring <strong>{pendingCountryChange.oldCountry}</strong> with {shortlistedUniversities.length} universities in your shortlist.
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <p className="text-xs text-amber-700">
                    <strong>Note:</strong> Switching countries will clear your current university shortlist. Your profile details (academics, English score, financials) will be preserved.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-100"
                    onClick={handleCountryChangeCancel}
                  >
                    Keep {pendingCountryChange.oldCountry}
                  </Button>
                  <Button
                    className="flex-1 bg-ux-blue hover:bg-ux-blue/90 text-white"
                    onClick={handleCountryChangeConfirm}
                  >
                    Switch to {pendingCountryChange.newCountry}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

export default FastTrackApplicationPage;
